import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { History, LocationState } from 'history';
import { SubmitHandler, UnpackNestedValue, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import axios, { AxiosError } from 'axios';
import Joi from 'joi';
import { useLocalStorage } from '@rehooks/local-storage';

// Components
import { Button, ButtonType, FormTextInput } from '../../../../shared';

// Fields
import { emailJoiStringField, passwordStringField } from '../../../../../utils';

// Hooks
import { useFormErrors, useQuery } from '../../../../hooks';

// Models
import { AppModel, AuthRequest, AuthResponse, Response } from '../../../../models';

// Redux
import { login } from '../../../../../request-agent';
import { updateUser } from '../../../../../redux';

// Routes
import routes from '../../../../../routes';
import { BASE_URL } from '../../../../../axios';

const formScheme: Joi.ObjectSchema<AuthRequest> = Joi.object({
  email: emailJoiStringField,
  password: passwordStringField,
});

export const LoginForm: FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const [ appUrl, setAppUrl ] = useState<string>('applications/screenshotsmaker-1.0.0.exe');
  const [ appVersion, setAppVersion ] = useState<string>('1.0.0');
  const query: URLSearchParams = useQuery();
  const history: History<LocationState> = useHistory();
  const [ , setJwt ] = useLocalStorage('token', '');
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid, isSubmitting },
  } = useForm<AuthRequest>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: joiResolver(formScheme),
  });
  const setFormErrors = useFormErrors(formScheme, setError);

  const [ formError, setFormError ] = useState({
    showError: false,
    message: '',
  });

  const onSubmit: SubmitHandler<AuthRequest> = async (formData: UnpackNestedValue<AuthRequest>): Promise<void> => {
    try {
      const response: Response<AuthResponse> = await login(formData);

      if (response?.data?.token) {
        dispatch(updateUser(response.data.user));
        setJwt(response.data.token);
        history.push({
          pathname: routes.list,
          search: query.has('app-auth') ? query.toString() : '',
        });
      }
    } catch (error: unknown) {
      const err = error as AxiosError<Response<void>>;
      if (err?.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }

      if (err.response?.data?.errors?.general?.length) {
        setFormError({
          showError: true,
          message: err.response.data.errors.general[0] || 'Wrong credentials',
        });
      }
    }
  };

  const checkAppVersion = async (): Promise<void> => {
    const version = await axios.get<Response<AppModel>>(`${ BASE_URL }/api/version`);
    setAppUrl(version?.data?.data?.app?.url || 'applications/screenshotsmaker-1.0.0.exe');
    setAppVersion(version?.data?.data?.app?.version || '1.0.0');
  };

  useEffect((): void => {
    checkAppVersion();
  }, []);

  return (
    <form className="auth-page-form" onSubmit={ handleSubmit(onSubmit) }>
      <p className={ `auth-page-form__error${ formError?.showError ? ' auth-page-form__error_show' : '' }` }>
        { formError?.message }
      </p>
      <div className="auth-page-form-fields">
        <FormTextInput register={ register('email') }
                       type="email"
                       placeholder="Email"
                       error={ errors.email?.message }
                       showError={ !!errors.email } />
        <FormTextInput register={ register('password') }
                       type="password"
                       placeholder="Password"
                       error={ errors.password?.message }
                       showError={ !!errors.password } />
      </div>
      <Link to={ routes.forgotPassword }
            className="link auth-page-form__reset-link">
        Reset password
      </Link>
      <Button submit
              className="auth-page-form__send-button"
              value="Sign In"
              disabled={ !isValid || isSubmitting } />
      <Button value={ `Download (${ appVersion } x64)` }
              className="auth-page-form__send-button"
              buttonType={ ButtonType.Gray }
              onClick={
                (): void => {
                  const element: HTMLAnchorElement = document.createElement('a');
                  element.href = `${ BASE_URL }/${ appUrl }`;
                  element.click();
                }
              } />
    </form>
  );
};
